import { useEffect, useMemo, useState } from 'react';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import type { ChatbotConversation, ChatbotConversationListResponse } from '@/models/chatbot';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback/useDebouncedCallback';
import { chatbotQueryKeys } from './chatbotQueryKeys';
import useChatbotHistoryApi from './useChatbotHistoryApi';

const PAGE_SIZE = 50;
const INITIAL_PAGE_PARAM: string | undefined = undefined;
const STALE_TIME_MS = 30_000;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_SEARCH_PAGES = 10;

interface UseConversationListParams {
  searchTerm?: string;
  enabled?: boolean;
}

interface UseConversationListReturn {
  conversations: ChatbotConversation[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isFetchNextPageError: boolean;
  refetch: () => Promise<unknown>;
  fetchNextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

const useConversationList = ({
  searchTerm = '',
  enabled = true,
}: UseConversationListParams = {}): UseConversationListReturn => {
  const { listConversations } = useChatbotHistoryApi();

  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);
  const { debouncedCallback: debounceTerm } = useDebouncedCallback(setDebouncedTerm, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    debounceTerm(searchTerm);
  }, [searchTerm, debounceTerm]);

  const query = useInfiniteQuery<
    ChatbotConversationListResponse,
    Error,
    InfiniteData<ChatbotConversationListResponse, string | undefined>,
    typeof chatbotQueryKeys.conversations,
    string | undefined
  >({
    queryKey: chatbotQueryKeys.conversations,
    queryFn: ({ pageParam }): Promise<ChatbotConversationListResponse> =>
      listConversations({ limit: PAGE_SIZE, nextToken: pageParam }),
    initialPageParam: INITIAL_PAGE_PARAM,
    getNextPageParam: (lastPage): string | undefined => lastPage.next_token ?? undefined,
    staleTime: STALE_TIME_MS,
    enabled,
    retry: false,
  });

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError } = query;
  const trimmedTerm = debouncedTerm.trim();
  const isSearching = trimmedTerm.length > 0;
  const loadedPages = query.data?.pages.length ?? 0;

  useEffect(() => {
    if (isSearching && hasNextPage && !isFetchingNextPage && !isFetchNextPageError && loadedPages < MAX_SEARCH_PAGES) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError, isSearching, loadedPages]);

  const conversations = useMemo<ChatbotConversation[]>(() => {
    const loaded = query.data?.pages.flatMap(page => page.conversations) ?? [];

    if (!trimmedTerm) {
      return loaded;
    }

    const term = trimmedTerm.toLowerCase();
    return loaded.filter(conversation => conversation.title.toLowerCase().includes(term));
  }, [query.data, trimmedTerm]);

  return {
    conversations,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetchNextPageError,
    refetch: query.refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export default useConversationList;
